/**
 * Map Selectors
 *
 * Atomic selectors for map state. All selectors return primitives for minimal
 * re-renders.
 *
 * @module store/selectors/mapSelectors
 */

import type { GeoMarkupStore } from '../index';
import {
  BASEMAPS,
  DEFAULT_BASEMAP_ID,
  getDefaultBasemapId,
  getVisibleBasemaps,
} from '../../config/maplibre';
import type { BasemapDefinition, BasemapId } from '../../config/maplibre';
import { selectCurrentWorkspaceId } from './authSelectors';
import {
  selectActiveWorkspaceCountryCode,
  selectActiveWorkspaceMapSettings,
} from './workspaceSelectors';

/**
 * Resolved runtime basemap, derived from the active workspace, its map
 * settings, the workspace country and the user's per-workspace preference.
 *
 * Priority: the user's per-workspace preference (if still on the allowlist) →
 * the workspace default basemap (`getDefaultBasemapId`) → `'auto'`. The result
 * is always clamped to an allowed basemap (`getVisibleBasemaps`, which also
 * gates the PL-only orthophoto for non-PL workspaces).
 *
 * On public share pages `map.publicBasemapContext` overrides workspace
 * resolution entirely: the viewer's pick (stored under the synthetic share
 * key) is clamped to the share's basemap allowlist.
 *
 * Returns a primitive, so consumers subscribe atomically and only re-render
 * when the resolved id actually changes. Replaces the former
 * `useWorkspaceBasemapResolver` write-back effect.
 */
export const selectResolvedBasemapId = (state: GeoMarkupStore): BasemapId => {
  const publicContext = state.map.publicBasemapContext;
  if (publicContext) {
    const preferred = state.map.userBasemapByWorkspace[publicContext.shareKey];
    if (preferred && publicContext.allowedBasemaps.includes(preferred)) {
      return preferred;
    }
    // `defaultBasemap` was already clamped to the allow-list on hydration.
    const { defaultBasemap, allowedBasemaps } = publicContext;
    return defaultBasemap ?? allowedBasemaps[0] ?? DEFAULT_BASEMAP_ID;
  }

  const currentWorkspaceId = selectCurrentWorkspaceId(state);
  const mapSettings = selectActiveWorkspaceMapSettings(state);
  const countryCode = selectActiveWorkspaceCountryCode(state);
  const preferred = currentWorkspaceId
    ? state.map.userBasemapByWorkspace[currentWorkspaceId]
    : undefined;

  const visibleIds = getVisibleBasemaps(mapSettings, countryCode).map(
    (basemap) => basemap.id
  );
  const defaultId = getDefaultBasemapId(mapSettings);
  let resolved =
    preferred && visibleIds.includes(preferred) ? preferred : defaultId;
  if (!visibleIds.includes(resolved)) {
    // No visible basemap matches (e.g. the whole allowlist is country-gated
    // out): honour the configured default before falling back to 'auto', so a
    // misconfigured workspace doesn't silently degrade to the light OSM style.
    resolved = visibleIds[0] ?? defaultId;
  }
  return resolved;
};

/**
 * Basemap definitions the current viewer may pick from. Public share pages
 * use the share's allowlist; the authenticated map uses the workspace
 * allowlist with country gating. Returns an array — subscribe with
 * `useShallow` (definitions are stable module-level objects).
 */
export const selectVisibleBasemapDefinitions = (
  state: GeoMarkupStore
): BasemapDefinition[] => {
  const publicContext = state.map.publicBasemapContext;
  if (publicContext) {
    return BASEMAPS.filter((basemap) =>
      publicContext.allowedBasemaps.includes(basemap.id)
    );
  }

  return getVisibleBasemaps(
    selectActiveWorkspaceMapSettings(state),
    selectActiveWorkspaceCountryCode(state)
  );
};

/**
 * Key under which the viewer's basemap pick is persisted in
 * `userBasemapByWorkspace`: the synthetic share key on public pages, the
 * active workspace id otherwise (null when no workspace is selected).
 */
export const selectBasemapPreferenceKey = (
  state: GeoMarkupStore
): string | null => {
  return (
    state.map.publicBasemapContext?.shareKey ??
    selectCurrentWorkspaceId(state) ??
    null
  );
};
